import { useState, useEffect, useCallback, useRef } from "react";
import { fmtDate, refIcon } from "../../helpers/util";
import { EventBadge } from "../../helpers/csshelper";
import { useTranslation } from "react-i18next";

 const platformIcon = (p) => {
  const icons = { mobile: "📱", desktop: "💻", tablet: "🖥️" };
  return icons[p] || "💻";
};
;
export default function DetailDrawer({ record, onClose }) {
     const {t} = useTranslation()
  useEffect(() => {
    const h = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [onClose]);

  if (!record) return null;

  const Section = ({ title, children }) => (
 
    <div>
      <p
        className='text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500
                    px-5 py-2 bg-gray-50 dark:bg-gray-800/70 border-y border-gray-100 dark:border-gray-700'
      >
        {title}
      </p>
      {children}
    </div>
  );

  const Row = ({ label, value, mono = false, highlight = false }) => (
    <div
      className='flex items-start gap-4 px-5 py-3 border-b border-gray-100 dark:border-gray-700/50
                    last:border-0 bg-white dark:bg-gray-900'
    >
      <span className='text-xs font-medium text-gray-400 dark:text-gray-500 min-w-[118px] shrink-0 pt-0.5'>
        {label}
      </span>
      <span
        className={`leading-relaxed break-all
        ${mono ? "font-mono text-[12px]" : "text-sm"}
        ${highlight ? "text-blue-600 dark:text-blue-400 font-semibold" : "text-gray-800 dark:text-gray-200"}
      `}
      >
        {value || "—"}
      </span>
    </div>
  );

  // events array safely handle karo
  const events = Array.isArray(record.events) ? record.events : [];
  const platform = record.device?.platform || "—";

  return (
    <>
      <div
        className='fixed inset-0 bg-black/40 dark:bg-black/60 backdrop-blur-[2px] z-40'
        onClick={onClose}
      />
      <div
        className='fixed right-0 top-0 h-full w-[440px] max-w-full bg-white dark:bg-gray-900
                   z-50 shadow-2xl flex flex-col border-l border-gray-200 dark:border-gray-700 overflow-hidden'
        style={{ animation: "slideIn 0.2s cubic-bezier(0.22,1,0.36,1)" }}
      >
        {/* Header */}
        <div
          className='px-5 py-4 border-b border-gray-200 dark:border-gray-700
                        bg-white dark:bg-gray-900 flex items-center justify-between shrink-0'
        >
          <div>
            <h2 className='text-base font-semibold text-gray-900 dark:text-gray-100'>
              {t('TRACKING.SESSION_DETAILS')}
            </h2>
            <p className='font-mono text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 truncate max-w-[330px]'>
              {record.sessionId}
            </p>
          </div>
          <button
            onClick={onClose}
            className='w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700
                       flex items-center justify-center text-gray-500 dark:text-gray-400 text-sm transition-colors'
          >
            ✕
          </button>
        </div>

        {/* Quick Stats */}
        <div
          className='grid grid-cols-3 border-b border-gray-200 dark:border-gray-700 shrink-0
                        divide-x divide-gray-200 dark:divide-gray-700'
        >
          {[
            { label: t('TRACKING.EVENTS'), value: events.length },
            { label: t('TRACKING.PLATFORM'), value: platform },
            { label: t('TRACKING.COUNTRY'), value: record.country || "—" },
          ].map((s) => (
            <div
              key={s.label}
              className='bg-white dark:bg-gray-900 px-4 py-3 text-center'
            >
              <p className='text-lg font-bold text-gray-900 dark:text-gray-100 leading-none capitalize'>
                {s.value}
              </p>
              <p className='text-[10px] text-gray-400 dark:text-gray-500 uppercase tracking-wider font-medium mt-1'>
                {s.label}
              </p>
            </div>
          ))}
        </div>

        {/* Scrollable Body */}
        <div className='flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-800/30'>
          <Section title={t('TRACKING.SESSION_DETAILS')}>
            <Row label={t('TRACKING.OBJECT_ID')} value={record._id} mono highlight />
            <Row label={t('TRACKING.SESSION_ID')} value={record.sessionId} mono />
            <Row label={t('TRACKING.BUSINESS_UUID')} value={record.businessUuid} mono />
            <Row label={t('TRACKING.BUSINESS_NAME')} value={record.businessName} />
          </Section>

          <Section title={t('TRACKING.DEVICE')}>
            <Row label={t('TRACKING.DEVICE_ID')} value={record.deviceId} mono />
            <Row label={t('TRACKING.DEVICE_TYPE')} value={record.deviceType ? `${platformIcon(record.deviceType)} ${record.deviceType}` : "—"} />
            <Row label={t('TRACKING.DEVICE_BRAND')} value={record.deviceBrand} />
            <Row label={t('TRACKING.DEVICE_MODEL')} value={record.deviceModel} highlight />
            <Row label={t('TRACKING.DEVICE_MANUFACTURER')} value={record.deviceManufacturer} />
            <Row label={t('TRACKING.DEVICE_ARCHITECTURE')} value={record.deviceArchitecture} />
            <Row label={t('TRACKING.CPU_CLASS')} value={record.cpuClass} />
            <Row label={t('TRACKING.HARDWARE_CONCURRENCY')} value={record.hardwareConcurrency} />
            <Row label={t('TRACKING.DEVICE_MEMORY')} value={record.deviceMemory ? `${record.deviceMemory} GB` : "—"} />
          </Section>

          <Section title='Operating System'>
            <Row label={t('TRACKING.OS_NAME')} value={record.osName} highlight />
            <Row label={t('TRACKING.OS_VERSION')} value={record.osVersion} />
            <Row label={t('TRACKING.OS_BUILD')} value={record.osBuildNumber} />
            <Row label={t('TRACKING.PLATFORM_TYPE')} value={record.platformType} />
            <Row label={t('TRACKING.SYSTEM_LANGUAGE')} value={record.systemLanguage} />
            <Row label={t('TRACKING.TIMEZONE')} value={record.timezone} />
            <Row label={t('TRACKING.LOCALE')} value={record.locale} />
          </Section>

          <Section title='Browser Details'>
            <Row label={t('TRACKING.BROWSER_NAME')} value={record.browserName} highlight />
            <Row label={t('TRACKING.BROWSER_VERSION')} value={record.browserVersion} mono />
            <Row label={t('TRACKING.USER_AGENT')} value={record.userAgentString} mono />
            <Row label={t('TRACKING.BROWSER_ENGINE')} value={record.browserEngine} />
            <Row label={t('TRACKING.COOKIES_ENABLED')} value={record.cookiesEnabled !== undefined ? (record.cookiesEnabled ? "Yes" : "No") : "—"} />
            <Row label={t('TRACKING.DO_NOT_TRACK')} value={record.doNotTrack} />
            <Row label={t('TRACKING.PLUGINS')} value={Array.isArray(record.plugins) ? record.plugins.join(", ") : record.plugins} />
            <Row label={t('TRACKING.WEBGL_RENDERER')} value={record.webglRenderer} />
            <Row label={t('TRACKING.WEBGL_VENDOR')} value={record.webglVendor} />
            <Row label={t('TRACKING.CANVAS_FINGERPRINT')} value={record.canvasFingerprint} mono />
            <Row label={t('TRACKING.AUDIO_FINGERPRINT')} value={record.audioFingerprint} />
          </Section>

          <Section title='Screen & Display'>
            <Row label={t('TRACKING.SCREEN_RESOLUTION')} value={record.screenResolution} />
            <Row label={t('TRACKING.AVAILABLE_RESOLUTION')} value={record.availableScreenResolution} />
            <Row label={t('TRACKING.COLOR_DEPTH')} value={record.colorDepth} />
            <Row label={t('TRACKING.PIXEL_RATIO')} value={record.pixelRatio} />
            <Row label={t('TRACKING.SCREEN_ORIENTATION')} value={record.screenOrientation} />
            <Row label={t('TRACKING.TOUCH_SUPPORT')} value={record.touchSupport ? "Yes" : "No"} />
            <Row label={t('TRACKING.MAX_TOUCH_POINTS')} value={record.maxTouchPoints} />
          </Section>

          <Section title='Connectivity'>
            <Row label={t('TRACKING.IP_ADDRESS')} value={record.ipAddress} mono highlight />
            <Row label={t('TRACKING.IPV6_ADDRESS')} value={record.ipv6Address} mono />
            <Row label={t('TRACKING.CONNECTION_TYPE')} value={record.connectionType} />
            <Row label={t('TRACKING.CARRIER_NAME')} value={record.carrierName} />
            <Row label={t('TRACKING.ASN')} value={record.asn} mono />
            <Row label={t('TRACKING.ISP')} value={record.ispName} />
          </Section>

          <Section title='Location Info'>
            <Row label={t('TRACKING.CITY')} value={record.city} highlight />
            <Row label={t('TRACKING.STATE')} value={record.state} />
            <Row label={t('TRACKING.REGION')} value={record.region} />
            <Row label={t('TRACKING.COUNTRY')} value={record.country} />
            <Row label={t('TRACKING.LATITUDE')} value={record.latitude} />
            <Row label={t('TRACKING.LONGITUDE')} value={record.longitude} />
            <Row label={t('TRACKING.TIMEZONE_OFFSET')} value={record.timezoneOffset} />
            <Row label={t('TRACKING.GPS_AVAILABLE')} value={record.gpsAvailable ? "Yes" : "No"} />
          </Section>

          <Section title='Status'>
            <Row label={t('TRACKING.IS_GUEST')} value={record.isGuest ? "Yes" : "No"} />
            <Row label={t('TRACKING.CONVERTED_AT')} value={fmtDate(record.convertedAt)} />
          </Section>

          <Section title='Traffic Source'>
            <Row
              label={t('TRACKING.REFERRER')}
              value={
                record.referrer
                  ? `${refIcon(record.referrer)} ${record.referrer === "direct" ? "Direct" : record.referrer}`
                  : "—"
              }
              mono={record.referrer && record.referrer !== "direct"}
            />
          </Section>

          <Section title='Timestamps'>
            <Row label={t('TRACKING.CREATED_AT')} value={fmtDate(record.createdAt)} mono />
            <Row label={t('TRACKING.FIRST_VISIT')} value={fmtDate(record.firstVisitAt)} mono />
            <Row label={t('TRACKING.LAST_SYNC')} value={fmtDate(record.lastSyncAt)} mono />
            <Row label={t('TRACKING.UPDATED_AT')} value={fmtDate(record.updatedAt)} mono />
          </Section>

          <Section title={`Events (${events.length})`}>
            {events.length === 0 ? (
              <p className='px-5 py-4 text-sm text-gray-400 dark:text-gray-500 bg-white dark:bg-gray-900'>
                {t('TRACKING.NO_EVENTS')}
              </p>
            ) : (
              events.map((ev, i) => (
                <div
                  key={i}
                  className='flex items-center gap-3 px-5 py-3 border-b border-gray-100
                                      dark:border-gray-700/50 last:border-0 bg-white dark:bg-gray-900'
                >
                  <EventBadge type={ev.type} />
                  <span className='font-mono text-xs text-blue-600 dark:text-blue-400 font-semibold'>
                    {ev.page}
                  </span>
                  <span className='font-mono text-[10px] text-gray-400 dark:text-gray-500 ml-auto'>
                    {fmtDate(ev.at)}
                  </span>
                </div>
              ))
            )}
          </Section>
        </div>
      </div>
      <style>{`@keyframes slideIn{from{transform:translateX(100%)}to{transform:translateX(0)}}`}</style>
    </>
  );
}